/**
 * 기능 목록 (Capability Catalog)
 *
 * 왜 만들었나:
 *   예전에는 시스템 프롬프트에 SP 이름 232개를 통째로 넣고 "알아서 골라 쓰라" 고 했다. 두 가지가 나빴다.
 *     1) 정확도 — 232개 중에서 고르는 것도 어렵고, SP 설명에 없는 값(@삭제여부1 같은 것)은 알 방법이 없다.
 *     2) 비용   — 그 목록이 2,984 토큰인데 매 턴마다 다시 나간다. 3턴이면 9천 토큰이 목록값이다.
 *
 *   그래서 "사용자가 실제로 하는 일" 단위로 목록을 만들고, AI 는 그중 하나를 고르기만 한다.
 *   SP 이름·파라미터 조립은 여기 적힌 레시피대로 코드가 한다. AI 가 @파라미터를 손으로 짜지 않는다.
 *
 * 두 종류가 있다. 이 구분이 핵심이다:
 *   via.type === 'sp'     — SP 를 정해진 레시피대로 부른다. (조회 대부분)
 *   via.type === 'screen' — 화면이 등록한 함수를 부른다. (screenTools.js)
 *                           가격/재고·명단처럼 화면만 아는 값(자원번호, 필드번호, 전체교체 규칙)이
 *                           필요한 일은 SP 레시피로 표현 자체가 안 되기 때문이다.
 *
 * 목록에 없는 일은 기존 search_procedures 로 SP 를 직접 찾는 길이 그대로 남아 있다.
 * (기능 목록은 "빠르고 정확한 길" 이지 유일한 길이 아니다.)
 *
 * ── 항목 쓰는 법 ─────────────────────────────────────────
 *   id       AI 가 부를 때 쓰는 이름. 바꾸면 안 된다.
 *   name     사람이 읽는 기능 이름.
 *   domain   업무 영역. 프롬프트에 실리는 짧은 힌트를 만드는 데 쓴다.
 *   keywords 사용자가 쓸 법한 말들. 검색용이라 넉넉히 적는다.
 *   what     이 기능이 무엇을 하는지 한 줄.
 *   kind     'read' 면 바로 실행, 'write' 면 사용자 승인 후 실행.
 *   inputs   AI 에게 받을 값들. [{ key, desc, required }]
 *   via      실행 방법 (위 두 종류)
 *   notes    AI 가 알아야 할 주의사항 (선택)
 */

/** 날짜 도우미 — 레시피에서 기본값을 만들 때 쓴다 */
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const today = () => ymd(new Date());
const monthStart = () => {
  const d = new Date();
  return ymd(new Date(d.getFullYear(), d.getMonth(), 1));
};
const monthEnd = () => {
  const d = new Date();
  return ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
};
const yearAgo = () => {
  const d = new Date();
  return ymd(new Date(d.getFullYear() - 1, d.getMonth(), d.getDate()));
};
const yearLater = () => {
  const d = new Date();
  return ymd(new Date(d.getFullYear() + 1, d.getMonth(), d.getDate()));
};

export const CAPABILITIES = [
  // ── 정산 · 수납 ──────────────────────────────────────
  {
    id: 'receivables_list',
    name: '미수 현황 조회',
    domain: '정산·수납',
    keywords: ['미수', '미수금', '못 받은 돈', '외상', '수금', '받을 돈', '연체'],
    what: '기간 안의 미수금(아직 못 받은 돈) 목록을 본다.',
    kind: 'read',
    inputs: [
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 이번 달 1일', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 이번 달 말일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_S_미수현황',
      params: [
        { name: '@시작일', from: '시작일', fallback: monthStart },
        { name: '@종료일', from: '종료일', fallback: monthEnd },
      ],
    },
  },
  {
    id: 'receipt_history',
    name: '수납 내역 조회',
    domain: '정산·수납',
    keywords: ['수납', '입금', '받은 돈', '결제 내역', '입금 내역', '통장'],
    what: '기간 안에 들어온 수납(입금) 내역을 본다.',
    kind: 'read',
    inputs: [
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 이번 달 1일', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 이번 달 말일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.CW_S_수납관리',
      params: [
        { name: '@시작일', from: '시작일', fallback: monthStart },
        { name: '@종료일', from: '종료일', fallback: monthEnd },
      ],
    },
  },

  // ── 상담 · 견적 ──────────────────────────────────────
  {
    id: 'consult_list',
    name: '상담(견적) 목록 조회',
    domain: '상담·견적',
    keywords: ['상담', '견적', '문의', '상담 목록', '견적 목록', '상담리스트'],
    what: '기간 안에 등록된 상담(견적) 목록을 본다.',
    kind: 'read',
    inputs: [
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 이번 달 1일', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 오늘', required: false },
      { key: '기준', desc: '"등록일" 또는 "출발일". 생략하면 등록일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.ITN_S_상담리스트',
      params: [
        { name: '@시작일', from: '시작일', fallback: monthStart },
        { name: '@종료일', from: '종료일', fallback: today },
        { name: '@구분', from: '기준', fallback: () => '등록일' },
        { name: '@상태', fixed: '4' }, // 4 = 전체
      ],
    },
    notes: '@상태 4는 전체를 뜻한다. 코드가 알아서 넣으므로 신경 쓰지 않아도 된다.',
  },
  {
    id: 'consult_detail',
    name: '상담 상세 조회',
    domain: '상담·견적',
    keywords: ['상담 상세', '견적 상세', '상담 내용', '이 상담', '상담번호'],
    what: '상담번호 하나의 상세 정보를 본다.',
    kind: 'read',
    inputs: [{ key: '상담번호', desc: '조회할 상담번호(숫자)', required: true }],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_S_상담정보',
      params: [
        { name: '@상담번호', from: '상담번호' },
        { name: '@구분', fixed: 'U' },
      ],
    },
  },
  {
    id: 'consult_notes',
    name: '상담 기록 조회',
    domain: '상담·견적',
    keywords: ['상담 기록', '상담 메모', '통화 기록', '히스토리', '상담 이력'],
    what: '상담 한 건에 남긴 기록(메모) 목록을 본다.',
    kind: 'read',
    inputs: [{ key: '상담번호', desc: '상담번호(숫자)', required: true }],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_S_상담번호_상담기록',
      params: [{ name: '@상담번호', from: '상담번호' }],
    },
  },
  {
    id: 'consult_note_add',
    name: '상담 기록 추가',
    domain: '상담·견적',
    keywords: ['상담 기록 남겨', '메모 남겨', '기록 추가', '통화했다고', '상담 내용 적어'],
    what: '상담 한 건에 새 기록(메모)을 남긴다.',
    kind: 'write',
    inputs: [
      { key: '상담번호', desc: '기록을 남길 상담번호(숫자)', required: true },
      { key: '내용', desc: '남길 내용', required: true },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_T_상담기록정보',
      params: [
        { name: '@상담번호', from: '상담번호' },
        { name: '@상담기록번호', fixed: '0' }, // 0 = 새로 추가
        { name: '@내용', from: '내용' },
      ],
    },
    notes: '@상담기록번호 0 은 "새로 추가" 를 뜻한다. 기존 기록을 고치는 기능이 아니다.',
  },

  // ── 예약 · 판매 ──────────────────────────────────────
  {
    id: 'order_status_list',
    name: '예약 현황 조회',
    domain: '예약·판매',
    keywords: ['예약', '예약 현황', '주문', '판매 현황', '예약 목록', '행사'],
    what: '기간 안의 예약(주문) 목록을 본다.',
    kind: 'read',
    inputs: [
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 이번 달 1일', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 이번 달 말일', required: false },
      { key: '기준', desc: '"행사일" 또는 "예약일". 생략하면 행사일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.WSP_S_예약현황_02',
      params: [
        // 검색기준은 SP 가 숫자 코드를 받는다. 사람 말("행사일")을 코드로 바꿔 준다.
        { name: '@검색기준', from: '기준', map: { 행사일: '2', 출발일: '2', 예약일: '1', 등록일: '1' }, fallback: () => '2' },
        { name: '@시작일', from: '시작일', fallback: monthStart },
        { name: '@종료일', from: '종료일', fallback: monthEnd },
        // ⚠ 이 네 개는 카탈로그에 기본값이 없어 AI 가 알 방법이 없던 값들이다. 코드가 고정으로 넣는다.
        { name: '@삭제여부1', fixed: 'false' },
        { name: '@삭제여부2', fixed: 'false' },
        { name: '@취소여부1', fixed: '0' },
        { name: '@취소여부2', fixed: '1' },
      ],
    },
  },
  {
    id: 'tour_product_list',
    name: '투어 상품 목록 조회',
    domain: '상품',
    keywords: ['상품', '투어 상품', '상품 목록', '판매 상품', '상품 리스트'],
    what: '우리 회사의 투어 상품 목록을 본다.',
    kind: 'read',
    inputs: [],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_투어상품리스트', params: [] },
  },

  // ── 화면에서만 되는 일 (via.type === 'screen') ─────────────
  {
    id: 'price_stock_apply',
    name: '가격·재고 일괄 적용',
    domain: '가격·재고',
    keywords: ['가격', '재고', '판매가', '성인가', '요금', '재고 추가', '가격 변경', '마감'],
    what: '보고 있는 상품의 날짜별 판매가와 재고를 한 번에 적용한다.',
    kind: 'write',
    screen: '가격/재고 설정',
    via: { type: 'screen', tool: 'price_stock_apply' },
    inputs: [
      { key: '시작일', desc: '적용 시작일 YYYY-MM-DD', required: false },
      { key: '종료일', desc: '적용 종료일 YYYY-MM-DD', required: false },
      { key: '성인가', desc: '성인 판매가(숫자만)', required: false },
      { key: '재고수', desc: '설정할 재고 수량', required: false },
    ],
    notes: '가격/재고 화면에서만 된다. 자원번호·가격번호는 화면이 알아서 채운다.',
  },
  {
    id: 'reservation_traveler_add',
    name: '예약 명단 인원 추가',
    domain: '예약·판매',
    keywords: ['명단', '인원 추가', '여행객 추가', '사람 추가', '인원 늘려', '명단 추가'],
    what: '보고 있는 예약의 명단에 인원을 추가한다. 이름까지 한 번에 넣을 수 있다.',
    kind: 'write',
    screen: '예약 상세',
    via: { type: 'screen', tool: 'reservation_traveler_add' },
    inputs: [
      { key: '이름목록', desc: '추가할 사람 이름들(배열). 모르면 비운다', required: false },
      { key: '인원수', desc: '이름 없이 자리만 늘릴 때의 인원수', required: false },
      { key: '연령', desc: '성인/아동/유아. 생략하면 성인', required: false },
    ],
    notes: '예약 상세 화면에서만 된다. 기존 명단은 그대로 유지된다.',
  },

  // ══ 여기서부터 2차 작성분 (30개) ══════════════════════════
  // 레시피의 고정값은 전부 화면 코드가 실제로 보내는 값을 확인해서 옮긴 것이다.
  // 자동으로 채워지는 값(@업체번호/@공급업체번호/@공급자번호/@유저번호/@처리자)은 여기 적지 않는다.

  // ── 정산 · 수납 ──────────────────────────────────────
  {
    id: 'cashbook_list',
    name: '통장 거래내역 조회',
    domain: '정산·수납',
    keywords: ['통장', '출납', '거래내역', '입출금', '계좌', '돈 들어온', '돈 나간'],
    what: '기간 안의 통장 입출금(출납) 내역을 본다.',
    kind: 'read',
    inputs: [
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 이번 달 1일', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 이번 달 말일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.WSP_S_출납리스트_02',
      params: [
        { name: '@시작일', from: '시작일', fallback: monthStart },
        { name: '@종료일', from: '종료일', fallback: monthEnd },
      ],
    },
  },
  {
    id: 'pg_transactions',
    name: 'PG 거래내역 조회',
    domain: '정산·수납',
    keywords: ['PG', '카드 결제', '온라인 결제', '결제 내역', '피지'],
    what: '기간 안의 PG(카드·온라인 결제) 거래내역을 본다.',
    kind: 'read',
    inputs: [
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 이번 달 1일', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 이번 달 말일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.RCT_S_PG거래내역',
      params: [
        { name: '@시작일', from: '시작일', fallback: monthStart },
        { name: '@종료일', from: '종료일', fallback: monthEnd },
      ],
    },
  },
  {
    id: 'sale_receipt_list',
    name: '예약 1건의 수납 내역 조회',
    domain: '정산·수납',
    keywords: ['이 예약 수납', '이 건 입금', '얼마 받았', '수납 얼마', '결제됐'],
    what: '예약(판매) 한 건에 들어온 수납 내역을 본다.',
    kind: 'read',
    inputs: [
      { key: '판매번호', desc: '예약의 판매번호(숫자)', required: true },
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 1년 전', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 오늘', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.WSP_S_수납리스트_01',
      params: [
        { name: '@판매번호', from: '판매번호' },
        { name: '@시작일', from: '시작일', fallback: yearAgo },
        { name: '@종료일', from: '종료일', fallback: today },
      ],
    },
  },
  {
    id: 'sale_deposit_list',
    name: '예약 1건의 입금 내역 조회',
    domain: '정산·수납',
    keywords: ['입금 내역', '입금 적용', '이 예약 입금', '잔금'],
    what: '예약(판매) 한 건에 적용된 입금 목록을 본다.',
    kind: 'read',
    inputs: [{ key: '판매번호', desc: '예약의 판매번호(숫자)', required: true }],
    via: {
      type: 'sp',
      procedureName: 'DBO.WSP_S_입금리스트',
      params: [{ name: '@판매번호', from: '판매번호' }],
    },
  },
  {
    id: 'group_settlement_main',
    name: '단체정산 목록 조회',
    domain: '정산·수납',
    keywords: ['단체정산', '정산', '행사 정산', '단체 결산', '결산'],
    what: '기간 안의 행사단체 결산(단체정산) 목록을 본다.',
    kind: 'read',
    inputs: [
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 이번 달 1일', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 이번 달 말일', required: false },
      { key: '기준', desc: '"행사일" 또는 "예약일". 생략하면 행사일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.WSP_S_행사단체결산_메인',
      params: [
        { name: '@시작일', from: '시작일', fallback: monthStart },
        { name: '@종료일', from: '종료일', fallback: monthEnd },
        { name: '@검색구분', from: '기준', fallback: () => '행사일' },
        { name: '@수배담당', fixed: 'NULL' },
      ],
    },
  },
  {
    id: 'group_settlement_stats',
    name: '단체정산 통계 조회',
    domain: '정산·수납',
    keywords: ['정산 통계', '단체 통계', '매출 통계', '실적'],
    what: '기간 안의 단체정산 통계(합계·건수)를 본다.',
    kind: 'read',
    inputs: [
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 이번 달 1일', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 이번 달 말일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.WSP_S_행사단체통계_02',
      params: [
        { name: '@시작일', from: '시작일', fallback: monthStart },
        { name: '@종료일', from: '종료일', fallback: monthEnd },
      ],
    },
  },

  // ── 예약 · 판매 ──────────────────────────────────────
  {
    id: 'order_info',
    name: '주문 정보 조회',
    domain: '예약·판매',
    keywords: ['주문번호', '주문 정보', '이 주문', '주문 상세'],
    what: '주문번호 하나의 주문 정보를 본다.',
    kind: 'read',
    inputs: [{ key: '주문번호', desc: '주문번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_주문조회_주문정보', params: [{ name: '@주문번호', from: '주문번호' }] },
  },
  {
    id: 'sale_info',
    name: '예약 판매정보 조회',
    domain: '예약·판매',
    keywords: ['판매 정보', '예약 상세', '이 예약 정보', '판매번호'],
    what: '판매번호 하나의 예약(판매) 상세 정보를 본다.',
    kind: 'read',
    inputs: [{ key: '판매번호', desc: '판매번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_예약관리_판매정보', params: [{ name: '@판매번호', from: '판매번호' }] },
  },
  {
    id: 'sale_price_info',
    name: '예약 금액 정보 조회',
    domain: '예약·판매',
    keywords: ['이 예약 금액', '판매가', '총액', '미수', '금액 정보', '얼마짜리'],
    what: '예약 한 건의 금액 정보(판매가·수납·미수)를 본다.',
    kind: 'read',
    inputs: [{ key: '판매번호', desc: '판매번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_예약관리_가격정보', params: [{ name: '@판매번호', from: '판매번호' }] },
  },
  {
    id: 'sale_traveler_list',
    name: '예약 명단 조회 (판매번호로)',
    domain: '예약·판매',
    keywords: ['명단', '여행객', '누가 가는지', '탑승자', '참가자', '명단 보여'],
    what: '판매번호로 그 예약의 여행객 명단을 본다.',
    kind: 'read',
    inputs: [{ key: '판매번호', desc: '판매번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.CW_S_예약관리_여행객리스트', params: [{ name: '@판매번호', from: '판매번호' }] },
  },
  {
    id: 'sale_extra_services',
    name: '예약 추가서비스 조회',
    domain: '예약·판매',
    keywords: ['추가서비스', '옵션', '부가', '추가 상품', '단품'],
    what: '예약 한 건에 붙은 추가서비스를 본다.',
    kind: 'read',
    inputs: [{ key: '판매번호', desc: '판매번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_예약관리_추가서비스', params: [{ name: '@판매번호', from: '판매번호' }] },
  },
  {
    id: 'sale_customer_requests',
    name: '고객 요청사항 조회',
    domain: '예약·판매',
    keywords: ['요청사항', '고객 요청', '특이사항', '요구사항', '메모'],
    what: '예약 한 건에 고객이 남긴 요청사항을 본다.',
    kind: 'read',
    inputs: [{ key: '판매번호', desc: '판매번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_판매_고객요청사항', params: [{ name: '@판매번호', from: '판매번호' }] },
  },
  {
    id: 'sale_cancel_info',
    name: '예약 상태·취소료 조회',
    domain: '예약·판매',
    keywords: ['취소료', '취소 수수료', '위약금', '예약 상태', '취소하면 얼마'],
    what: '예약 한 건의 현재 상태와 취소 시 취소료를 본다.',
    kind: 'read',
    inputs: [{ key: '판매번호', desc: '판매번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_예약상태및취소료가져오기', params: [{ name: '@판매번호', from: '판매번호' }] },
  },
  {
    id: 'sale_logs',
    name: '예약 변경 기록 조회',
    domain: '예약·판매',
    keywords: ['변경 기록', '이력', '누가 고쳤', '수정 이력', '변경 이력', '로그', '히스토리'],
    what: '예약 한 건에 누가 무엇을 바꿨는지 기록을 본다.',
    kind: 'read',
    inputs: [{ key: '판매번호', desc: '판매번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_예약기록_판매번호', params: [{ name: '@판매번호', from: '판매번호' }] },
  },
  {
    id: 'group_profit',
    name: '단체 손익 조회',
    domain: '예약·판매',
    keywords: ['손익', '이익', '마진', '남는 돈', '수익', '원가'],
    what: '예약이 속한 행사단체의 손익 정보를 본다.',
    kind: 'read',
    inputs: [{ key: '판매번호', desc: '판매번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_예약관리_단체손익', params: [{ name: '@판매번호', from: '판매번호' }] },
  },
  {
    id: 'group_event_info',
    name: '행사단체 정보 조회',
    domain: '예약·판매',
    keywords: ['행사단체', '단체 정보', '단체번호', '행사 정보'],
    what: '예약이 속한 행사단체의 기본 정보를 본다.',
    kind: 'read',
    inputs: [
      { key: '판매번호', desc: '판매번호(숫자)', required: true },
      { key: '단체번호', desc: '단체번호. 모르면 비운다', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_S_예약관리_행사단체정보',
      params: [
        { name: '@판매번호', from: '판매번호' },
        { name: '@단체번호', from: '단체번호', fallback: () => 'NULL' },
      ],
    },
  },
  {
    id: 'product_order_status',
    name: '상품별 예약 현황 조회',
    domain: '예약·판매',
    keywords: ['이 상품 예약', '상품별 예약', '몇 명 붙었', '모객', '판매 현황'],
    what: '상품 하나에 기간 안에 들어온 예약 목록을 본다.',
    kind: 'read',
    inputs: [
      { key: '상품번호', desc: '상품번호(숫자)', required: true },
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 오늘', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 1년 뒤', required: false },
      { key: '기준', desc: '"행사일" 또는 "예약일". 생략하면 행사일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.WSP_S_예약현황_02_상품번호',
      params: [
        { name: '@검색기준', from: '기준', map: { 행사일: '2', 출발일: '2', 예약일: '1', 등록일: '1' }, fallback: () => '2' },
        { name: '@시작일', from: '시작일', fallback: today },
        { name: '@종료일', from: '종료일', fallback: yearLater },
        { name: '@삭제여부1', fixed: 'false' },
        { name: '@삭제여부2', fixed: 'false' },
        { name: '@취소여부1', fixed: '0' },
        { name: '@취소여부2', fixed: '1' },
        { name: '@상품번호', from: '상품번호' },
      ],
    },
  },

  // ── 상품 ────────────────────────────────────────────
  {
    id: 'product_detail',
    name: '상품 상세정보 조회',
    domain: '상품',
    keywords: ['상품 상세', '상품 정보', '이 상품 뭐', '상품 내용'],
    what: '투어 상품 하나의 상세 정보를 본다.',
    kind: 'read',
    inputs: [{ key: '상품번호', desc: '상품번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_투어_상세정보', params: [{ name: '@상품번호', from: '상품번호' }] },
  },
  {
    id: 'itinerary_type_list',
    name: '일정표 종류 목록 조회',
    domain: '상품',
    keywords: ['일정표 종류', '일정 타입', '일정표 목록', '코스'],
    what: '상품에 등록된 일정표 종류를 본다. 일정 상세를 보려면 여기서 일정타입번호를 먼저 얻는다.',
    kind: 'read',
    inputs: [{ key: '상품번호', desc: '상품번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.WSP_S_상품일정타입리스트', params: [{ name: '@상품번호', from: '상품번호' }] },
  },
  {
    id: 'itinerary_detail',
    name: '일정표 상세 조회',
    domain: '상품',
    keywords: ['일정표', '일정 상세', '며칠째', '몇 박', '코스 내용', '스케줄'],
    what: '상품의 일자별 일정 내용을 본다.',
    kind: 'read',
    inputs: [
      { key: '상품번호', desc: '상품번호(숫자)', required: true },
      { key: '일정타입번호', desc: '일정표 종류 목록 조회로 먼저 얻는다', required: true },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.RCT_S_상품일정_02',
      params: [
        { name: '@상품번호', from: '상품번호' },
        { name: '@일정타입번호', from: '일정타입번호' },
      ],
    },
    notes: '일정타입번호를 모르면 itinerary_type_list 를 먼저 부른다.',
  },
  {
    id: 'product_options',
    name: '상품 선택옵션 조회',
    domain: '상품',
    keywords: ['선택옵션', '옵션', '추가 선택', '고를 수 있는'],
    what: '상품에 등록된 선택옵션 목록을 본다.',
    kind: 'read',
    inputs: [{ key: '상품번호', desc: '상품번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_선택옵션', params: [{ name: '@상품번호', from: '상품번호' }] },
  },
  {
    id: 'product_resources',
    name: '항공패턴·자원 목록 조회',
    domain: '상품',
    keywords: ['항공패턴', '자원', '패턴', '항공', '자원번호'],
    what: '상품의 항공패턴(자원) 목록을 본다. 요금제나 재고를 보려면 여기서 자원번호를 먼저 얻는다.',
    kind: 'read',
    inputs: [{ key: '상품번호', desc: '상품번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.RCT_S_투어상품자원리스트', params: [{ name: '@상품번호', from: '상품번호' }] },
  },
  {
    id: 'rate_plans',
    name: '요금제 목록 조회',
    domain: '상품',
    keywords: ['요금제', '가격옵션', '요금 종류', '플랜'],
    what: '상품의 특정 항공패턴에 달린 요금제 목록을 본다.',
    kind: 'read',
    inputs: [
      { key: '상품번호', desc: '상품번호(숫자)', required: true },
      { key: '자원번호', desc: '항공패턴·자원 목록 조회로 먼저 얻는다', required: true },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.RCT_S_투어요금제리스트_02',
      params: [
        { name: '@상품번호', from: '상품번호' },
        { name: '@자원번호', from: '자원번호' },
      ],
    },
    notes: '자원번호를 모르면 product_resources 를 먼저 부른다.',
  },
  {
    id: 'hotel_room_list',
    name: '호텔 객실상품 목록 조회',
    domain: '상품',
    keywords: ['객실', '호텔 룸', '방 종류', '룸타입', '호텔 상품'],
    what: '호텔 상품의 객실상품 목록을 본다.',
    kind: 'read',
    inputs: [{ key: '상품번호', desc: '상품번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_호텔룸상품리스트', params: [{ name: '@상품번호', from: '상품번호' }] },
  },

  // ── 재고 ────────────────────────────────────────────
  {
    id: 'tour_stock_list',
    name: '투어 재고 조회',
    domain: '가격·재고',
    keywords: ['재고', '남은 자리', '좌석', '몇 자리', '잔여', '재고 현황'],
    what: '상품의 기간별 재고(남은 자리)를 본다.',
    kind: 'read',
    inputs: [
      { key: '상품번호', desc: '상품번호(숫자)', required: true },
      { key: '자원번호', desc: '항공패턴·자원 목록 조회로 먼저 얻는다', required: true },
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 오늘', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 1년 뒤', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_S_투어재고리스트_자원기간_상품번호',
      params: [
        { name: '@상품번호', from: '상품번호' },
        { name: '@시작일', from: '시작일', fallback: today },
        { name: '@종료일', from: '종료일', fallback: yearLater },
        { name: '@시작시간', fixed: '00:00' },
        { name: '@자원번호', from: '자원번호' },
      ],
    },
    notes: '가격/재고 화면을 보고 있다면 화면 전용 도구(price_stock_read)가 더 간단하다.',
  },
  {
    id: 'shared_inventory',
    name: '공유재고 목록 조회',
    domain: '가격·재고',
    keywords: ['공유재고', '시리즈 재고', '공유 좌석', '블록'],
    what: '상품에 연결된 공유재고(시리즈) 목록을 본다.',
    kind: 'read',
    inputs: [{ key: '상품번호', desc: '상품번호(숫자)', required: true }],
    via: { type: 'sp', procedureName: 'DBO.TRP_S_공유재고', params: [{ name: '@상품번호', from: '상품번호' }] },
  },
  {
    id: 'series_management',
    name: '시리즈 관리 조회',
    domain: '가격·재고',
    keywords: ['시리즈', '시리즈 관리', '차수', '출발 회차'],
    what: '기간 안의 시리즈(공유재고 묶음) 목록을 본다.',
    kind: 'read',
    inputs: [
      { key: '시작일', desc: 'YYYY-MM-DD. 생략하면 이번 달 1일', required: false },
      { key: '종료일', desc: 'YYYY-MM-DD. 생략하면 이번 달 말일', required: false },
      { key: '기준', desc: '"출발일" 또는 "등록일". 생략하면 출발일', required: false },
      { key: '시리즈번호', desc: '특정 시리즈만 볼 때. 생략하면 전체', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_S_시리즈관리',
      params: [
        { name: '@시작일', from: '시작일', fallback: monthStart },
        { name: '@종료일', from: '종료일', fallback: monthEnd },
        { name: '@검색기준', from: '기준', fallback: () => '출발일' },
        { name: '@시리즈번호', from: '시리즈번호', fallback: () => '0' },
      ],
    },
  },

  // ── 거래처 · 사원 ────────────────────────────────────
  {
    id: 'supplier_list',
    name: '공급사 목록 조회',
    domain: '거래처·사원',
    keywords: ['공급사', '공급업체', '거래처', '랜드사', '협력사', '도매'],
    what: '우리와 거래하는 공급사(공급 거래처) 목록을 본다.',
    kind: 'read',
    inputs: [],
    via: {
      type: 'sp',
      procedureName: 'DBO.CW_S_공급거래처',
      params: [
        { name: '@사용여부1', fixed: 'true' },
        { name: '@사용여부2', fixed: 'true' },
      ],
    },
  },
  {
    id: 'client_list',
    name: '판매 거래처 목록 조회',
    domain: '거래처·사원',
    keywords: ['판매처', '판매 거래처', '대리점', '여행사', '고객사', '거래처'],
    what: '우리 상품을 파는 판매 거래처(대리점) 목록을 본다.',
    kind: 'read',
    inputs: [],
    via: {
      type: 'sp',
      procedureName: 'DBO.CW_S_판매거래처',
      params: [
        { name: '@사용여부1', fixed: 'true' },
        { name: '@사용여부2', fixed: 'true' },
      ],
    },
  },
  {
    id: 'staff_list',
    name: '사원 목록 조회',
    domain: '거래처·사원',
    keywords: ['사원', '직원', '담당자', '우리 직원', '팀원'],
    what: '우리 회사 사원 목록을 본다.',
    kind: 'read',
    inputs: [],
    via: { type: 'sp', procedureName: 'DBO.CW_S_사원', params: [{ name: '@상태', fixed: 'NULL' }] },
  },

  // ══ 3차 작성분 — 저장 기능 확장 ═══════════════════════════════
  // 그전까지 저장 기능이 3개뿐이라 "조회만 되는 도우미" 였다. 저장 SP 139개를 훑어 아래 기준으로 골랐다.
  //
  //   넣은 것 — 파라미터가 전부 "사람이 말할 수 있는 값" 이고, 대상 식별자(판매번호·상담번호·상품번호)를
  //             조회 기능으로 먼저 알아낼 수 있는 것.
  //   뺀 것   — ① 화면만 아는 큰 JSON 이 필요한 것 (@가격정보, @여행객_JSON, @상담정보, @요금표데이터,
  //                @선택행사, @상세정보). 이건 화면 도구(useScreenTools)로만 가능하다.
  //             ② 화면이 "지금 값" 을 읽어서 같이 보내야 하는 것. 예: 행사단체 일정/출발 확정은
  //                @확정확정표시여부에 기존 값을 실어 보내야 하는데 AI 는 그 값을 알 수 없고,
  //                모르고 보내면 옆에 있는 다른 토글이 조용히 뒤집힌다.
  //             ③ 순서 변경·이미지 매핑·파일 업로드처럼 대화로 시킬 일이 없는 것.
  //             ④ 파라미터가 20개를 넘어 되묻기만 열 번 하게 되는 것 (사원 등록/수정, 시리즈 날짜 편집).
  //
  // 고정값은 전부 화면 코드가 실제로 보내는 값을 확인해서 옮긴 것이다.

  // ── 예약 · 판매 ──────────────────────────────────────
  {
    id: 'reservation_memo_add',
    name: '예약 메모 남기기',
    domain: '예약·판매',
    keywords: ['예약 메모', '메모 남겨', '메모 추가', '기록 남겨', '예약 기록', '특이사항'],
    what: '예약(판매) 한 건에 메모를 남긴다. 예약 상세의 기록 탭에 쌓인다.',
    kind: 'write',
    inputs: [
      { key: '판매번호', desc: '메모를 남길 판매번호(숫자)', required: true },
      { key: '메모', desc: '남길 내용', required: true },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_T_예약메모기록',
      params: [
        { name: '@판매번호', from: '판매번호' },
        { name: '@메모', from: '메모' },
        // 화면 기본값과 동일 — 판매업체·수배업체 모두 볼 수 있게 남긴다.
        { name: '@판매업체조회가능', fixed: '1' },
        { name: '@수배업체조회가능', fixed: '1' },
      ],
    },
    notes: '주문번호가 아니라 판매번호다. 모르면 예약 현황 조회로 먼저 찾는다.',
  },
  {
    id: 'sale_memo_save',
    name: '판매 비고 저장',
    domain: '예약·판매',
    keywords: ['판매 비고', '비고 저장', '비고 수정', '예약 비고'],
    what: '예약(판매) 한 건의 비고란을 덮어쓴다.',
    kind: 'write',
    inputs: [
      { key: '판매번호', desc: '판매번호(숫자)', required: true },
      { key: '비고', desc: '저장할 비고 내용. 기존 내용은 지워지고 이 값으로 바뀐다', required: true },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_T_판매비고저장',
      params: [
        { name: '@판매번호', from: '판매번호' },
        { name: '@판매비고', from: '비고' },
      ],
    },
    notes: '덮어쓰기다. 기존 비고에 덧붙이려면 먼저 조회해서 합친 전체 문장을 넘겨야 한다.',
  },
  {
    id: 'sale_status_set',
    name: '예약 상태 변경',
    domain: '예약·판매',
    keywords: ['예약 상태', '상태 변경', '확정으로', '취소접수', '취소확정', '예약확정', '예약불가'],
    what: '예약(판매)의 상태를 바꾼다.',
    kind: 'write',
    inputs: [
      { key: '판매번호', desc: '판매번호(숫자)', required: true },
      {
        key: '상태',
        desc: '예약접수 / 예약확정 / 예약불가 / 취소접수 / 취소확정 / 취소완료 중 하나. 이 여섯 낱말만 쓴다.',
        required: true,
      },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_T_예약관리_판매상태',
      // 상태는 코드 하나가 아니라 3단계 누적값이다(화면도 이렇게 보낸다).
      // 한 낱말을 세 파라미터로 푸는 것이라 map 세 벌이 필요하고, 여섯 낱말이 모두 들어 있어야 한다.
      // 빠진 낱말이 있으면 그 말이 코드 대신 그대로 서버로 나간다.
      params: [
        { name: '@판매번호', from: '판매번호' },
        {
          name: '@예약상태1',
          from: '상태',
          map: {
            예약접수: '049001', 접수: '049001',
            예약확정: '049001', 확정: '049001',
            예약불가: '049001', 불가: '049001',
            취소접수: '049021', 취소: '049021',
            취소확정: '049021', 취소완료: '049021',
          },
        },
        {
          name: '@예약상태2',
          from: '상태',
          map: {
            예약접수: '', 접수: '',
            예약확정: '049011', 확정: '049011',
            예약불가: '049031', 불가: '049031',
            취소접수: '', 취소: '',
            취소확정: '049022', 취소완료: '049022',
          },
        },
        {
          name: '@예약상태3',
          from: '상태',
          map: {
            예약접수: '', 접수: '',
            예약확정: '', 확정: '',
            예약불가: '', 불가: '',
            취소접수: '', 취소: '',
            취소확정: '', 취소완료: '049029',
          },
        },
      ],
    },
    notes: '취소완료로 바꿔도 취소료는 따로다. 취소료는 "취소료 수정" 기능을 쓴다.',
  },
  {
    id: 'orderer_info_update',
    name: '주문자 정보 수정',
    domain: '예약·판매',
    keywords: ['주문자', '예약자 정보', '연락처 수정', '주문자 이메일', '주문자 전화'],
    what: '주문 한 건의 주문자 이름·휴대폰·이메일을 고친다.',
    kind: 'write',
    inputs: [
      { key: '주문번호', desc: '주문번호. 판매번호가 아니다', required: true },
      { key: '주문자', desc: '주문자 이름', required: false },
      { key: '휴대폰', desc: '주문자 휴대폰', required: false },
      { key: '이메일', desc: '주문자 이메일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_T_예약관리_주문_주문자정보',
      params: [
        { name: '@주문번호', from: '주문번호' },
        { name: '@주문자', from: '주문자' },
        { name: '@주문자휴대폰', from: '휴대폰' },
        { name: '@주문자이메일', from: '이메일' },
      ],
    },
    notes: '세 값을 통째로 덮어쓴다. 하나만 고칠 때도 나머지 두 개를 기존 값 그대로 함께 넘겨야 한다. 기존 값은 주문 정보 조회로 먼저 확인한다.',
  },
  {
    id: 'cancel_fee_update',
    name: '취소료 수정',
    domain: '예약·판매',
    keywords: ['취소료', '위약금', '취소 수수료', '취소료 변경'],
    what: '예약(판매) 한 건의 부과 취소료 금액을 고친다.',
    kind: 'write',
    inputs: [
      { key: '판매번호', desc: '판매번호(숫자)', required: true },
      { key: '취소료', desc: '부과할 취소료 금액(숫자만)', required: true },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.CW_T_예약관리_취소료수정',
      params: [
        { name: '@판매번호', from: '판매번호' },
        { name: '@취소료', from: '취소료' },
      ],
    },
  },
  {
    id: 'agency_commission_update',
    name: '대리점 커미션 수정',
    domain: '예약·판매',
    keywords: ['커미션', '대리점 수수료', '커미션 수정', '수수료 변경'],
    what: '예약(판매) 한 건의 대리점 커미션 금액을 고친다.',
    kind: 'write',
    inputs: [
      { key: '판매번호', desc: '판매번호(숫자)', required: true },
      { key: '금액', desc: '커미션 금액(숫자만)', required: true },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.CW_T_예약관리_대리점커미션수정',
      params: [
        { name: '@판매번호', from: '판매번호' },
        { name: '@금액', from: '금액' },
      ],
    },
  },
  {
    id: 'reservation_restore',
    name: '취소 예약 복원',
    domain: '예약·판매',
    keywords: ['예약 복원', '취소 취소', '되살려', '복구', '취소 철회'],
    what: '취소한 예약을 다시 살린다.',
    kind: 'write',
    inputs: [{ key: '판매번호', desc: '복원할 판매번호(숫자)', required: true }],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_T_취소상품예약복원',
      params: [{ name: '@판매번호', from: '판매번호' }],
    },
    notes: '재고가 다시 차감된다. 복원 대상이 맞는지 사용자에게 분명히 확인시킨다.',
  },
  {
    id: 'traveler_lock_set',
    name: '명단 수정 잠금',
    domain: '예약·판매',
    keywords: ['명단 잠금', '명단 잠가', '명단 락', '수정 못하게', '명단 풀어'],
    what: '예약 명단을 더 못 고치게 잠그거나 다시 푼다.',
    kind: 'write',
    inputs: [
      { key: '판매번호', desc: '판매번호(숫자)', required: true },
      { key: '잠금', desc: '"잠금" 또는 "해제"', required: true },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.WSP_T_판매_명단락여부',
      params: [
        { name: '@판매번호', from: '판매번호' },
        {
          name: '@상태',
          from: '잠금',
          map: { 잠금: 'true', 잠가: 'true', 잠금해제: 'false', 해제: 'false', 풀기: 'false', 풀어: 'false' },
        },
      ],
    },
  },
  {
    id: 'reservation_traveler_rename',
    name: '예약 명단 이름 수정',
    domain: '예약·판매',
    keywords: ['명단 이름', '이름 바꿔', '이름 수정', '오타', '여행객 이름'],
    what: '보고 있는 예약의 명단에서 한 사람의 한글 이름을 바꾼다.',
    kind: 'write',
    screen: '예약 상세',
    via: { type: 'screen', tool: 'reservation_traveler_rename' },
    inputs: [
      { key: '이름', desc: '새 한글 이름', required: true },
      { key: '여행객번호', desc: '대상 여행객번호. 명단 조회로 먼저 확인', required: false },
      { key: '순번', desc: '여행객번호를 모를 때 쓰는 명단 순번(1부터)', required: false },
      { key: '기존이름', desc: '번호·순번을 모를 때 쓰는 현재 이름. 같은 이름이 여럿이면 실패한다', required: false },
    ],
    notes: '예약 상세 화면에서만 된다. 대상을 셋 중 하나로는 반드시 지정해야 한다.',
  },

  // ── 상담 · 견적 ──────────────────────────────────────
  {
    id: 'consult_note_delete',
    name: '상담 기록 삭제',
    domain: '상담·견적',
    keywords: ['상담 기록 삭제', '기록 지워', '메모 삭제', '잘못 남긴'],
    what: '상담에 남긴 기록(메모) 한 건을 지운다.',
    kind: 'write',
    inputs: [{ key: '상담기록번호', desc: '지울 기록의 상담기록번호. 상담 기록 조회로 먼저 확인', required: true }],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_T_상담기록정보삭제',
      params: [{ name: '@상담기록번호', from: '상담기록번호' }],
    },
    notes: '되돌릴 수 없다. 지울 기록의 내용을 사용자에게 보여주고 확인받은 뒤 진행한다.',
  },

  // ── 상품 ────────────────────────────────────────────
  {
    id: 'product_note_save',
    name: '상품 비고 저장',
    domain: '상품',
    keywords: ['상품 비고', '상품 메모', '상품 특이사항'],
    what: '상품 한 건의 비고란을 덮어쓴다.',
    kind: 'write',
    inputs: [
      { key: '상품번호', desc: '상품번호(숫자)', required: true },
      { key: '비고', desc: '저장할 비고 내용', required: true },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_T_상품비고저장',
      params: [
        { name: '@상품번호', from: '상품번호' },
        { name: '@상품비고', from: '비고' },
      ],
    },
    notes: '덮어쓰기다. 덧붙이려면 기존 내용을 합친 전체 문장을 넘긴다.',
  },
  {
    id: 'product_content_list',
    name: '상세페이지 항목 목록',
    domain: '상품',
    keywords: ['상세페이지', '상품 소개', '콘텐츠 목록', '안내 문구', '포함사항', '취소 규정 항목'],
    what: '상품 상세페이지에 들어 있는 항목(콘텐츠) 목록을 본다. 항목을 고치려면 여기서 컨텐츠번호를 먼저 얻는다.',
    kind: 'read',
    inputs: [{ key: '상품번호', desc: '상품번호(숫자)', required: true }],
    via: {
      type: 'sp',
      procedureName: 'DBO.RCT_S_콘텐츠메인리스트',
      params: [{ name: '@상품번호', from: '상품번호' }],
    },
  },
  {
    id: 'product_content_save',
    name: '상세페이지 항목 저장',
    domain: '상품',
    keywords: ['상세페이지 등록', '상세페이지 수정', '안내문 등록', '취소환불 규정 등록', '포함사항 등록', '콘텐츠 저장'],
    what: '상품 상세페이지에 항목을 새로 넣거나 기존 항목을 고친다. (예: "취소 및 환불 규정")',
    kind: 'write',
    inputs: [
      { key: '상품번호', desc: '상품번호(숫자)', required: true },
      { key: '제목', desc: '항목 제목. 예: 취소 및 환불 규정', required: true },
      { key: '내용', desc: '본문. 문단은 <p>…</p>, 줄바꿈은 <br> 로 적으면 상세페이지에 그대로 보인다', required: true },
      { key: '컨텐츠번호', desc: '기존 항목을 고칠 때만. 비우면 새 항목으로 추가된다. 번호는 상세페이지 항목 목록으로 확인', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.RCT_T_상품정보콘텐츠저장',
      params: [
        { name: '@컨텐츠명', from: '제목' },
        { name: '@컨텐츠내용', from: '내용' },
        { name: '@컨텐츠내용HTML', from: '내용' },
        { name: '@상품번호', from: '상품번호' },
        // 화면과 동일 — 'NULL' 문자열이 "새로 추가" 를 뜻한다. JS null 을 보내면 0 이 되어 조용히 0건 수정된다.
        { name: '@컨텐츠번호', from: '컨텐츠번호', fallback: () => 'NULL' },
        { name: '@노출여부', fixed: 'true' },
        { name: '@대리점만노출', fixed: 'false' },
        { name: '@이미지매핑사용여부', fixed: 'true' },
        { name: '@파일첨부여부', fixed: 'false' },
      ],
    },
    notes:
      '여기 넣은 글은 고객이 보는 상세페이지에 그대로 실린다. 약관·취소규정처럼 시스템 밖에서 가져온 문구라면 '
      + '저장하기 전에 원문을 확인했는지 사용자에게 먼저 물어본다. 컨텐츠번호를 주면 그 항목을 통째로 덮어쓴다.',
  },

  // ── 정산 · 수납 ──────────────────────────────────────
  {
    id: 'reservation_receipt_add',
    name: '예약 입금(수납) 처리',
    domain: '정산·수납',
    keywords: ['입금', '입금 처리', '입금 등록', '수납', '수납 등록', '통장 입금', '돈 받았', '입금됐', '결제 받았'],
    what: '보고 있는 예약의 입금을 처리한다. 회사 통장에 입금 거래를 기록하고 그 금액을 이 예약의 입금내역에 반영한다.',
    kind: 'write',
    screen: '예약 상세',
    via: { type: 'screen', tool: 'reservation_receipt_add' },
    inputs: [
      { key: '금액', desc: '입금액(숫자만)', required: true },
      {
        key: '계좌일련번호',
        desc: '입금받은 회사 계좌. reservation_bank_accounts 로 목록을 읽어 사용자에게 고르게 한 값. 추측하지 말 것',
        required: true,
      },
      { key: '입금자', desc: '입금한 사람 이름. 생략하면 예약자 이름', required: false },
      { key: '수납일', desc: '입금일 YYYY-MM-DD. 생략하면 오늘', required: false },
      { key: '비고', desc: '통장·입금내역에 함께 남길 메모', required: false },
      { key: '즉시적용', desc: '"false" 면 통장에만 기록하고 예약에는 반영하지 않는다. 생략하면 반영', required: false },
    ],
    notes:
      '예약 상세 화면에서만 된다. 어느 계좌로 받았는지는 반드시 사용자가 고르게 한다 — 계좌가 하나뿐이어도 확인을 받고, '
      + '등록된 계좌가 없으면 등록을 시도하지 말고 "관리 → 통장관리" 에서 계좌부터 추가하라고 안내한다. '
      + '이것은 통장 장부에 기록하는 일이지 실제로 돈이 들어왔는지 확인하는 일이 아니다. '
      + '반영액은 미수금과 거래 잔액을 넘지 않게 잘리고, 남는 금액은 통장에 남아 다른 예약에 쓸 수 있다.',
  },
  {
    id: 'other_income_save',
    name: '기타수익 등록',
    domain: '정산·수납',
    keywords: ['기타수익', '수익 등록', '추가 수익', '부대수익'],
    what: '행사단체에 기타수익 한 건을 등록하거나 고친다.',
    kind: 'write',
    inputs: [
      { key: '행사단체번호', desc: '행사단체번호(숫자)', required: true },
      { key: '항목', desc: '수익 항목명', required: true },
      { key: '금액', desc: '금액(숫자만)', required: true },
      { key: '담당자', desc: '담당자명. 모르면 비운다', required: false },
      { key: '비고', desc: '비고', required: false },
      { key: '기타수익번호', desc: '기존 건을 고칠 때만. 비우면 새로 등록된다', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.TRP_T_기타수익',
      params: [
        { name: '@행사단체번호', from: '행사단체번호' },
        { name: '@항목', from: '항목' },
        { name: '@금액', from: '금액' },
        { name: '@담당자', from: '담당자' },
        { name: '@비고', from: '비고' },
        { name: '@기타수익번호', from: '기타수익번호', fallback: () => 'NULL' },
        { name: '@통화코드', fixed: '058001' }, // 원화
        { name: '@적용환율', fixed: '1' },
        { name: '@세금계산서여부', fixed: 'false' },
      ],
    },
    notes: '원화 기준으로만 등록된다. 외화 건은 화면에서 처리해야 한다.',
  },

  // ── 거래처 ──────────────────────────────────────────
  {
    id: 'supplier_add',
    name: '공급거래처 등록',
    domain: '거래처·사원',
    keywords: ['거래처 등록', '공급처 추가', '랜드사 등록', '거래처 추가'],
    what: '공급거래처를 새로 등록한다.',
    kind: 'write',
    inputs: [
      { key: '거래처명', desc: '거래처 이름', required: true },
      { key: '담당자명', desc: '담당자 이름', required: false },
      { key: '이메일', desc: '담당자 이메일', required: false },
    ],
    via: {
      type: 'sp',
      procedureName: 'DBO.WSP_T_공급거래처_등록',
      params: [
        { name: '@거래처명', from: '거래처명' },
        { name: '@담당자명', from: '담당자명' },
        { name: '@이메일', from: '이메일' },
      ],
    },
    notes: '이름만으로 간단히 등록된다. 주소·계좌 같은 나머지 정보는 거래처 관리 화면에서 채워야 한다.',
  },
];

const byId = new Map(CAPABILITIES.map((c) => [c.id, c]));

/** id 로 기능을 찾는다 */
export const findCapability = (id) => byId.get(String(id || '').trim()) || null;

/**
 * 시스템 프롬프트에 넣을 짧은 힌트.
 * 상세(파라미터·주의사항)는 넣지 않는다. AI 가 search_capabilities 로 그때 가져간다.
 * 이게 예전 SP 목록 2,984 토큰을 대신한다.
 */
export function buildCapabilityHint() {
  const domains = new Map();
  for (const c of CAPABILITIES) {
    if (!domains.has(c.domain)) domains.set(c.domain, []);
    domains.get(c.domain).push(c.name);
  }
  const lines = [...domains.entries()].map(([d, names]) => `- ${d}: ${names.join(', ')}`);
  return `[할 수 있는 일]\n${lines.join('\n')}`;
}

/**
 * 검색 점수: 이름·키워드·설명 순으로 가중치를 준다.
 *
 * ⚠ 키워드 점수는 합산하지 않고 "가장 잘 맞는 하나"만 센다.
 *   합산하면 "내역" 같은 흔한 낱말이 키워드에 여러 번 들어간 기능이 무조건 이긴다.
 *   (수납 내역·입금 내역·결제 내역을 다 적어둔 기능이 "수정 내역" 검색을 가로챘다.)
 */
function score(cap, needle) {
  const n = needle.toLowerCase();
  let s = 0;
  if (cap.name.toLowerCase() === n) s += 16;
  else if (cap.name.toLowerCase().includes(n)) s += 10;
  if (cap.id.toLowerCase().includes(n)) s += 8;

  let best = 0;
  for (const k of cap.keywords || []) {
    const kk = k.toLowerCase();
    if (kk === n) best = Math.max(best, 9);
    else if (kk.includes(n) || n.includes(kk)) best = Math.max(best, 6);
  }
  s += best;

  if ((cap.what || '').toLowerCase().includes(n)) s += 3;
  if ((cap.domain || '').toLowerCase().includes(n)) s += 2;
  return s;
}

/** 키워드로 기능을 찾아 AI 가 바로 쓸 수 있는 형태로 돌려준다 */
export function searchCapabilities(keyword, { limit = 4 } = {}) {
  const words = String(keyword || '').split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return CAPABILITIES.map((cap) => ({ cap, s: words.reduce((sum, w) => sum + score(cap, w), 0) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map(({ cap }) => describeCapability(cap));
}

/** AI 에게 보여줄 기능 설명 (실행에 필요한 것만) */
export function describeCapability(cap) {
  return {
    id: cap.id,
    기능: cap.name,
    설명: cap.what,
    종류: cap.kind === 'write' ? '저장/수정 (사용자 승인 필요)' : '조회',
    필요한값: (cap.inputs || []).map((i) => `${i.key}${i.required ? '(필수)' : ''} — ${i.desc}`),
    ...(cap.screen ? { 이_화면에서만: cap.screen } : {}),
    ...(cap.notes ? { 참고: cap.notes } : {}),
  };
}

/**
 * 기능 레시피대로 SP 파라미터를 만든다.
 * AI 가 준 값은 from 으로만 들어오고, fixed 는 코드가 정한 값이라 AI 가 바꿀 수 없다.
 */
export function buildSpParams(cap, values = {}) {
  const given = values && typeof values === 'object' ? values : {};
  return (cap.via.params || []).map((p) => {
    if (p.fixed !== undefined) return { name: p.name, value: p.fixed };
    let raw = given[p.from];
    if (raw === undefined || raw === null || String(raw).trim() === '') {
      raw = typeof p.fallback === 'function' ? p.fallback() : '';
    }
    let value = String(raw);
    if (p.map) value = p.map[value] ?? value;
    return { name: p.name, value };
  });
}
